import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";

export const meta: MetaFunction = ({ data, params }: { data: any, params: any }) => {
  const title = `About - Related Repos`;
  const description = 'About the site - how it works, how you can use it, how to provide feedback. Discover related open source projects';
  const canonicalURL = `${__BASE_URL__}/about`;
  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonicalURL,
    "author": {
      "@type": "Person",
      "name": "Patrick Lurch",
      "url": "https://github.com/plurch"
    },
  };

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonicalURL },
    { "script:ld+json": jsonLD },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalURL },
    { property: "og:image", content: __OG_IMAGE__ }
  ];
};

export default function Index() {
  return (
    <>
      <div className="background-gray px-2 py-2 border border-gray-100 rounded-md">
        <div className="flex items-center py-1">
          <h1 className="text-xl font-bold mr-1 ">
            About
          </h1>
        </div>
      </div>

      <div className="max-w-prose mx-auto pb-10 px-2 py-2">
        <p className="mt-2">
          <strong>Related Repos</strong> helps developers to discover open source projects that are related to each other. 
          This can be useful to find alternative or complementary packages when building a full application.
          Or you can simply surf around in different neighborhoods to find new ideas that you might not have been aware of.
        </p>

        <h2 className="mt-4 font-semibold">How it works</h2>

        <p className="mt-2">
          Data and results are updated daily so you can keep coming back to discover fresh repositories. Results can be thought of as "users who expressed interest in this repository also expressed interest in these other repositories".
        </p>

        <h2 className="mt-4 font-semibold">Technical details</h2>

        <p className="mt-2">
          The way that the results are generated is as follows:
        </p>

        <ol className="list-decimal pl-6 mt-2">
          <li>Offline processing: <a className="text-rr-link" href="https://simonwillison.net/2023/Oct/23/embeddings/" target="_blank">Embeddings</a> are computed for all repos based on user interaction activity (eg. starring a repo).</li>
          <li>At query time: The most similar repos are computed using <Link className="text-rr-link" to="/gh/erikbern/ann-benchmarks">approximate nearest neighbors</Link>.</li>
        </ol>

        <p className="mt-2">
          The returned list is <span className="underline">not</span> sorted by most stars. It is sorted by most similar repository according to the model, so the most relevant results should be at the top rather than the most popular.
        </p>

        <p className="mt-2">
          Models and approaches are continually evaluated in order to improve the usefulness of results.
        </p>

        <h2 className="mt-4 font-semibold">Search</h2>
        
        <p className="mt-2">
          Use the <Link to="/search" className="text-rr-link">search</Link> functionality to find an initial list of repositories as a starting point and then branch out to explore different neighborhoods. Search for anything - keyword and semantic search are supported.
        </p>

        <h2 className="mt-4 font-semibold">Browser Extension</h2>
        
        <p className="mt-2">
          Use a <Link to="/extension" className="text-rr-link">browser extension</Link> to quickly view related repos while browsing on GitHub.
        </p>

        <h2 className="mt-4 font-semibold">Popular Topics</h2>
        <p className="mt-2">
          Browse popular topics on the <Link to="/" className="text-rr-link">home page</Link>. Suggestions for new topics to add are welcomed.
        </p>

        <h2 className="mt-4 font-semibold">Feedback and discussion</h2>
        <p className="mt-2">
          Any type of <a className="text-rr-link" href="https://github.com/RelatedRepos/discussion/discussions/new?category=general-feedback" target="_blank">feedback</a> is welcomed - the goals is to improve the offering for all users! Please feel free to open a discussion about improvements to user experience, results, or new feature ideas.
        </p>

        <h2 className="mt-4 font-semibold">Builder</h2>
        <p className="mt-2">
          Patrick Lurch
        </p>

        <div className="mt-1 grid grid-cols-[25px_auto] gap-y-1 items-center">
          <div>
            {/* https://github.com/feathericons/feather/blob/main/icons/github.svg */}
            <svg className="mr-1" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
            </svg>
          </div>
          <div>
            <a href="https://github.com/plurch" target="_blank">@plurch</a>
          </div>
          <div>
            {/* icon from github profile link */}
            <svg className="ml-0.5" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" role="img" aria-labelledby="a648dkr2ibj6btuxluzd0wk54p1l871v">
              <title id="a648dkr2ibj6btuxluzd0wk54p1l871v">X</title>
              <path d="M9.52217 6.77143L15.4785 0H14.0671L8.89516 5.87954L4.76437 0H0L6.24656 8.8909L0 15.9918H1.41155L6.87321 9.78279L11.2356 15.9918H16L9.52183 6.77143H9.52217ZM7.58887 8.96923L6.95596 8.0839L1.92015 1.03921H4.0882L8.15216 6.7245L8.78507 7.60983L14.0677 14.9998H11.8997L7.58887 8.96957V8.96923Z" fill="currentColor"></path>
            </svg>
          </div>
          <div>
            <a href="https://x.com/RelatedRepos" target="_blank">@RelatedRepos</a>
          </div>
        </div>
      </div>
    </>
  );
}
